import { AllowanceStep } from './allowance-flow'
import { AllowanceForm, emptyForm } from './allowance-form'

export type DraftField = keyof AllowanceForm | 'address_state'

export type AllowanceDraft = Partial<AllowanceForm> & {
  address_state?: string
}

const storageKey = (applicationKey: string) => `credit-draft:${applicationKey}`

export const stepFields: Partial<Record<AllowanceStep, DraftField[]>> = {
  fac: ['collegeKey', 'collegePrivate', 'graduation'],
  plano: ['amount', 'grace', 'combo', 'tuition'],
  eu: ['birthdate', 'gender'],
  end: [
    'zip_code',
    'street',
    'district',
    'number',
    'complement',
    'municipality_id',
    'address_state',
  ],
  civil: ['marital', 'spouse_name', 'spouse_tax_id', 'politically_exposed', 'us_person'],
  pais: [
    'father_not_registered',
    'father_name',
    'father_tax_id',
    'father_education_level',
    'mother_name',
    'mother_tax_id',
    'mother_education_level',
  ],
  uso: ['purposes', 'purpose_other'],
  renda: [
    'scholarships',
    'scholarships_other',
    'scholarship_percentage',
    'financing',
    'financing_other',
    'financing_percentage',
    'payment_sources',
    'income',
  ],
  msg: ['free_text'],
  traj: [
    'high_school_type',
    'academic_award',
    'academic_award_description',
    'course_locked',
    'course_locked_reason',
  ],
  planos: ['plans', 'residency_timing'],
  conta: [
    'bank_code',
    'agency_number',
    'agency_digit',
    'account_number',
    'account_digit',
    'account_type',
  ],
}

function accept(key: keyof AllowanceForm, value: unknown) {
  const sample = emptyForm()[key]
  if (typeof sample === 'boolean') return typeof value === 'boolean' ? value : undefined
  if (typeof sample === 'number') {
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined
  }
  if (Array.isArray(sample)) {
    return Array.isArray(value) && value.every((item) => typeof item === 'string') ? value : undefined
  }
  return typeof value === 'string' ? value : undefined
}

export function readDraft(applicationKey: string): AllowanceDraft {
  try {
    const raw = localStorage.getItem(storageKey(applicationKey))
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    const draft: AllowanceDraft = {}
    const record = parsed as Record<string, unknown>
    for (const key of Object.keys(emptyForm()) as (keyof AllowanceForm)[]) {
      if (!(key in record)) continue
      const value = accept(key, record[key])
      if (value !== undefined) {
        Object.assign(draft, { [key]: value })
      }
    }
    if (typeof record.address_state === 'string') draft.address_state = record.address_state
    return draft
  } catch {
    return {}
  }
}

export function writeDraft(applicationKey: string, partial: AllowanceDraft) {
  const next = { ...readDraft(applicationKey), ...partial }
  localStorage.setItem(storageKey(applicationKey), JSON.stringify(next))
}

export function clearDraftFields(applicationKey: string, fields: DraftField[]) {
  const current = readDraft(applicationKey)
  for (const field of fields) delete current[field]
  if (Object.keys(current).length === 0) {
    localStorage.removeItem(storageKey(applicationKey))
    return
  }
  localStorage.setItem(storageKey(applicationKey), JSON.stringify(current))
}

export function clearDraft(applicationKey: string) {
  localStorage.removeItem(storageKey(applicationKey))
}

export function mergeDraft(base: AllowanceForm, draft: AllowanceDraft): AllowanceForm {
  const { address_state: _addressState, ...fields } = draft
  return { ...base, ...fields }
}
